import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  CircularProgress,
  Fade,
  Skeleton,
  Typography,
  SxProps,
  Theme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  UniqueIdentifier,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { SearchInputFactory, SearchableColumn } from "../SearchInputs";
import { debounce } from "../../../utils/debounce";
import {
  DATA_TABLE_ROWS_PER_PAGE_OPTIONS,
  DEFAULT_DATA_TABLE_PAGE_SIZE,
} from "../../../constants/dataTable";

export type SortDirection = "asc" | "desc" | null;

export interface SortModel {
  field: string;
  direction: SortDirection;
}

export interface Column {
  field: string;
  headerName: string;
  flex?: number;
  width?: number;
  minWidth?: number;
  maxWidth?: number;
  disableTooltip?: boolean;
  tooltipValue?: (value: any, row: any) => React.ReactNode;
  renderCell?: (value: any, row: any) => React.ReactNode;
  sortable?: boolean;
  sortType?: "string" | "number" | "date";
}

export interface Action<T> {
  icon: React.ReactNode;
  label: string;
  onClick: (item: T) => void;
  disabled?: boolean | ((item: T) => boolean);
  color?:
    | "inherit"
    | "primary"
    | "secondary"
    | "error"
    | "info"
    | "success"
    | "warning";
}

export interface DataTableGrouping<T> {
  getGroupKey: (item: T) => string;
  renderGroupHeader: (groupKey: string, groupItems: T[]) => React.ReactNode;
  collapsedItemLimit?: number;
  allowFullCollapse?: boolean;
  defaultExpanded?: boolean;
  groupLabel?: string;
  itemLabel?: string;
  countMode?: "groups" | "items";
}

export interface DataTableProps<T> {
  items: T[];
  columns: Column[];
  actions?: Action<T>[] | ((item: T) => Action<T>[]);
  isLoading?: boolean;
  title?: string;
  page?: number;
  totalCount?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  rowsPerPageOptions?: number[];
  getRowSx?: (row: T) => SxProps<Theme>;
  hasNextPage?: boolean;
  isLoadingMore?: boolean;
  isLoadingRows?: boolean;
  onLoadMore?: () => void;
  fitContentHeight?: boolean;
  enableRowReorder?: boolean;
  onReorder?: (orderedIds: (number | string)[]) => void;
  getRowId?: (row: T) => number | string;
  grouping?: DataTableGrouping<T>;
  toolbar?: React.ReactNode;

  // Search functionality
  enableSearch?: boolean;
  searchableColumns?: SearchableColumn[];
  initialSearchFilters?: Record<string, any>;
  onSearchChange?: (filters: Record<string, any>) => void;

  // Column sorting (client-side)
  enableSorting?: boolean;

  // Column reorder (drag-and-drop)
  enableColumnReorder?: boolean;
  onColumnOrderChange?: (fields: string[]) => void;
}

const TABLE_BOTTOM_MARGIN = 24;
const REORDER_COLUMN_WIDTH = 48;
const FIT_CONTENT_MAX_VIEWPORT_HEIGHT_RATIO = 0.75;

const getPrimitiveText = (value: unknown) => {
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  return null;
};

const isTooltipValuePresent = (value: React.ReactNode) =>
  value !== null && value !== undefined && value !== "";

const hasHorizontalOverflow = (element: HTMLElement) => {
  if (element.scrollWidth > element.clientWidth + 1) {
    return true;
  }

  return Array.from(element.querySelectorAll<HTMLElement>("*")).some(
    (child) => child.scrollWidth > child.clientWidth + 1
  );
};

const getBoundedContainerBottom = (element: HTMLElement) => {
  let current = element.parentElement;
  let nestedPaddingBottom = 0;

  while (current && current !== document.body) {
    const style = window.getComputedStyle(current);
    const paddingBottom = parseFloat(style.paddingBottom) || 0;
    const overflowY = style.overflowY;
    const hasBoundedOverflow =
      overflowY === "auto" || overflowY === "scroll" || overflowY === "hidden";
    const hasExplicitHeight =
      style.height !== "auto" || style.maxHeight !== "none";

    if (hasBoundedOverflow && hasExplicitHeight) {
      const rect = current.getBoundingClientRect();

      return rect.bottom - paddingBottom - nestedPaddingBottom;
    }

    nestedPaddingBottom += paddingBottom;
    current = current.parentElement;
  }

  return window.innerHeight - TABLE_BOTTOM_MARGIN;
};

interface TruncatedTooltipCellProps {
  children: React.ReactNode;
  tooltipTitle?: React.ReactNode;
  fallbackText?: string | null;
  maxWidth?: number;
}

const TruncatedTooltipCell: React.FC<TruncatedTooltipCellProps> = ({
  children,
  tooltipTitle,
  fallbackText,
  maxWidth,
}) => {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const [renderedText, setRenderedText] = useState("");

  const measureOverflow = useCallback(() => {
    const element = contentRef.current;
    if (!element) {
      return;
    }

    setIsTruncated(hasHorizontalOverflow(element));
    setRenderedText(element.textContent?.trim() ?? "");
  }, []);

  useEffect(() => {
    measureOverflow();

    const element = contentRef.current;
    if (!element) {
      return;
    }

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measureOverflow);
      return () => window.removeEventListener("resize", measureOverflow);
    }

    const observer = new ResizeObserver(measureOverflow);
    observer.observe(element);
    Array.from(element.querySelectorAll<HTMLElement>("*")).forEach((child) =>
      observer.observe(child)
    );

    return () => observer.disconnect();
  }, [children, measureOverflow]);

  const resolvedTooltipTitle = isTooltipValuePresent(tooltipTitle)
    ? tooltipTitle
    : fallbackText || renderedText;

  const wrappedContent = (
    <Box
      ref={contentRef}
      sx={{
        minWidth: 0,
        maxWidth: maxWidth ? `${maxWidth}px` : "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        "& .MuiTypography-root": {
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        },
        "& .MuiBox-root": {
          minWidth: 0,
        },
      }}
    >
      {children}
    </Box>
  );

  return isTruncated && isTooltipValuePresent(resolvedTooltipTitle) ? (
    <Tooltip title={resolvedTooltipTitle} placement="top" arrow>
      {wrappedContent}
    </Tooltip>
  ) : (
    wrappedContent
  );
};

interface SortableTableRowProps {
  id: UniqueIdentifier;
  sx: SxProps<Theme>;
  children: (dragHandleProps: {
    attributes: any;
    listeners?: any;
    isDragging: boolean;
  }) => React.ReactNode;
}

const SortableTableRow: React.FC<SortableTableRowProps> = ({
  id,
  sx,
  children,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  return (
    <TableRow
      ref={setNodeRef}
      sx={sx}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.75 : undefined,
        position: isDragging ? "relative" : undefined,
        zIndex: isDragging ? 5 : undefined,
      }}
    >
      {children({ attributes, listeners, isDragging })}
    </TableRow>
  );
};

export const DataTable = <T,>({
  items,
  columns,
  actions,
  isLoading = false,
  title,
  page = 0,
  totalCount = 0,
  pageSize = DEFAULT_DATA_TABLE_PAGE_SIZE,
  onPageChange,
  onPageSizeChange,
  rowsPerPageOptions = DATA_TABLE_ROWS_PER_PAGE_OPTIONS,
  getRowSx,
  hasNextPage = false,
  isLoadingMore = false,
  isLoadingRows = false,
  onLoadMore,
  fitContentHeight = false,
  enableRowReorder = false,
  onReorder,
  getRowId,
  grouping,
  toolbar,

  // Search functionality
  enableSearch = false,
  searchableColumns = [],
  initialSearchFilters,
  onSearchChange,

  // Sorting
  enableSorting = false,

  // Column reorder
  enableColumnReorder = false,
  onColumnOrderChange,
}: DataTableProps<T>) => {
  const ACTIONS_COLUMN_WIDTH = 144;
  const MIN_TABLE_HEIGHT = 320;
  const LONG_TEXT_COLUMN_LENGTH_THRESHOLD = 48;
  const LONG_TEXT_AVERAGE_LENGTH_THRESHOLD = 28;
  const COMPACT_COLUMN_LENGTH_THRESHOLD = 16;
  const COLUMN_SIZING_SAMPLE_LIMIT = 100;
  const MIN_SKELETON_ROWS = 5;
  const MAX_SKELETON_ROWS = 12;
  const SKELETON_ANIMATION_DURATION = "1s";
  const isGroupingEnabled = Boolean(grouping);
  const isRowReorderEnabled =
    enableRowReorder && Boolean(onReorder) && !isGroupingEnabled;
  const isSearchEnabled = enableSearch && !isRowReorderEnabled;
  const hasActions =
    actions && (typeof actions === "function" || actions.length > 0);
  const totalColumns =
    columns.length + (hasActions ? 1 : 0) + (isRowReorderEnabled ? 1 : 0);
  const displayedTotalCount = totalCount ?? items.length;
  const skeletonRowCount = Math.min(
    Math.max(items.length || pageSize, MIN_SKELETON_ROWS),
    MAX_SKELETON_ROWS
  );
  const stickyActionsCellSx = {
    position: "sticky",
    right: 0,
    width: ACTIONS_COLUMN_WIDTH,
    minWidth: ACTIONS_COLUMN_WIDTH,
    maxWidth: ACTIONS_COLUMN_WIDTH,
    borderLeft: 1,
    borderLeftColor: "divider",
    boxShadow: "-8px 0 10px -10px rgba(0,0,0,0.55)",
  } as const;

  // Sort state
  const [sortModel, setSortModel] = useState<SortModel>({ field: "", direction: null });

  // Column order state
  const [columnOrder, setColumnOrder] = useState<string[]>(() =>
    columns.map((c) => c.field)
  );

  useEffect(() => {
    setColumnOrder((prev) => {
      const incomingFields = columns.map((c) => c.field);
      const reordered = prev.filter((f) => incomingFields.includes(f));
      const added = incomingFields.filter((f) => !prev.includes(f));
      return [...reordered, ...added];
    });
  }, [columns]);

  const orderedColumns = useMemo(() => {
    if (!enableColumnReorder) return columns;
    const colMap = new Map(columns.map((c) => [c.field, c]));
    return columnOrder.map((f) => colMap.get(f)).filter(Boolean) as Column[];
  }, [columns, columnOrder, enableColumnReorder]);

  const displayColumns = enableColumnReorder ? orderedColumns : columns;

  const handleSortClick = useCallback((field: string) => {
    setSortModel((prev) => {
      if (prev.field !== field) return { field, direction: "asc" };
      if (prev.direction === "asc") return { field, direction: "desc" };
      return { field: "", direction: null };
    });
  }, []);

  const sortedItems = useMemo(() => {
    if (!enableSorting || !sortModel.field || !sortModel.direction) return items;
    const col = columns.find((c) => c.field === sortModel.field);
    if (!col) return items;

    const sortType = col.sortType ?? "string";
    const dir = sortModel.direction === "asc" ? 1 : -1;

    return [...items].sort((a, b) => {
      const av = (a as any)[sortModel.field];
      const bv = (b as any)[sortModel.field];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;

      if (sortType === "number") return (Number(av) - Number(bv)) * dir;
      if (sortType === "date") return (new Date(av).getTime() - new Date(bv).getTime()) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
  }, [items, sortModel, enableSorting, columns]);

  const handleColumnDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      setColumnOrder((prev) => {
        const oldIndex = prev.indexOf(String(active.id));
        const newIndex = prev.indexOf(String(over.id));
        const next = arrayMove(prev, oldIndex, newIndex);
        onColumnOrderChange?.(next);
        return next;
      });
    },
    [onColumnOrderChange]
  );

  const columnSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  // Search state management
  const [searchFilters, setSearchFilters] = useState<Record<string, any>>(
    () => initialSearchFilters ?? {},
  );
  const [isSearchPending, setIsSearchPending] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const hasActiveSearchFilters = Object.keys(searchFilters).length > 0;
  const showRowSkeletons = isLoading || isLoadingRows || isSearchPending;
  const [availableTableHeight, setAvailableTableHeight] = useState<number | null>(
    null
  );
  const paperRef = useRef<HTMLDivElement | null>(null);
  const headerContainerRef = useRef<HTMLDivElement | null>(null);
  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const loadMoreSentinelRef = useRef<HTMLTableRowElement | null>(null);
  const loadMoreRequestedRef = useRef(false);
  const searchRequestDispatchedRef = useRef(false);
  const sensors = useSensors(useSensor(PointerSensor));

  // Ref to store the debounced callback
  const debouncedOnSearchChangeRef = useRef<
    ((filters: Record<string, any>) => void) | null
  >(null);

  // Create debounced callback for parent notification (400ms delay)
  useEffect(() => {
    if (onSearchChange) {
      debouncedOnSearchChangeRef.current = debounce(
        (filters: Record<string, any>) => {
          searchRequestDispatchedRef.current = true;
          onSearchChange(filters);
        },
        400
      );
    }
    return () => {
      // Cleanup: cancel any pending debounced calls on unmount
      if (
        debouncedOnSearchChangeRef.current &&
        (debouncedOnSearchChangeRef.current as any).cancel
      ) {
        (debouncedOnSearchChangeRef.current as any).cancel();
      }
    };
  }, [onSearchChange]);

  useEffect(() => {
    if (
      !isSearchPending ||
      !searchRequestDispatchedRef.current ||
      isLoading ||
      isLoadingRows
    ) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setIsSearchPending(false);
      searchRequestDispatchedRef.current = false;
    }, 100);

    return () => window.clearTimeout(timeoutId);
  }, [isLoading, isLoadingRows, isSearchPending, items]);

  // Handle search filter changes with smart debouncing
  const handleSearchChange = useCallback(
    (
      field: string,
      value: any,
      searchType?: "string" | "number" | "date" | "select"
    ) => {
      setSearchFilters((prevFilters) => {
        const newFilters = { ...prevFilters };

        if (value === null || value === undefined || value === "") {
          delete newFilters[field];
        } else {
          newFilters[field] = value;
        }

        // Notify parent component
        if (onSearchChange) {
          // Immediate update for select inputs and date inputs (user explicitly selected a value)
          // Debounced update for string and number inputs (user is typing)
          if (searchType === "select" || searchType === "date") {
            setIsSearchPending(true);
            searchRequestDispatchedRef.current = true;
            onSearchChange(newFilters);
          } else {
            // Debounce string and number inputs
            // Show loading indicator while waiting
            setIsSearchPending(true);
            searchRequestDispatchedRef.current = false;

            if (debouncedOnSearchChangeRef.current) {
              debouncedOnSearchChangeRef.current(newFilters);
            }
          }
        }

        return newFilters;
      });
    },
    [onSearchChange]
  );

  // Clear all search filters (immediate, no debounce)
  const clearAllFilters = useCallback(() => {
    // Cancel any pending debounced calls
    if (
      debouncedOnSearchChangeRef.current &&
      (debouncedOnSearchChangeRef.current as any).cancel
    ) {
      (debouncedOnSearchChangeRef.current as any).cancel();
    }

    setSearchFilters({});
    if (onSearchChange) {
      setIsSearchPending(true);
      searchRequestDispatchedRef.current = true;
      onSearchChange({});
    } else {
      setIsSearchPending(false);
    }
  }, [onSearchChange]);
  const handlePageChange = (event: unknown, newPage: number) => {
    onPageChange?.(newPage);
  };

  const handlePageSizeChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    onPageSizeChange?.(parseInt(event.target.value, 10));
  };

  const resolveRowId = useCallback(
    (item: T, index: number): UniqueIdentifier => {
      if (getRowId) {
        return getRowId(item);
      }

      return (item as any).id ?? index;
    },
    [getRowId]
  );

  const sortableIds = useMemo(
    () => items.map((item, index) => resolveRowId(item, index)),
    [items, resolveRowId]
  );

  const groupedItems = useMemo(() => {
    if (!grouping) {
      return [];
    }

    const groups: { key: string; items: T[] }[] = [];
    const groupMap = new Map<string, T[]>();

    items.forEach((item) => {
      const groupKey = grouping.getGroupKey(item);
      const existingGroup = groupMap.get(groupKey);

      if (existingGroup) {
        existingGroup.push(item);
        return;
      }

      const newGroup = [item];
      groupMap.set(groupKey, newGroup);
      groups.push({ key: groupKey, items: newGroup });
    });

    return groups;
  }, [grouping, items]);

  const displayedGroupTotalCount = totalCount ?? groupedItems.length;
  const groupLabel = grouping?.groupLabel ?? "groups";
  const groupedItemLabel = grouping?.itemLabel ?? "row";
  const groupingCountMode = grouping?.countMode ?? "groups";

  const toggleGroup = useCallback((groupKey: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }

      return next;
    });
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id || !onReorder) {
        return;
      }

      const oldIndex = sortableIds.indexOf(active.id);
      const newIndex = sortableIds.indexOf(over.id);
      if (oldIndex < 0 || newIndex < 0) {
        return;
      }

      onReorder(arrayMove(sortableIds, oldIndex, newIndex));
    },
    [onReorder, sortableIds]
  );

  const handleBodyScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (headerContainerRef.current) {
      headerContainerRef.current.scrollLeft = event.currentTarget.scrollLeft;
    }
  };

  useEffect(() => {
    if (!isLoadingMore) {
      loadMoreRequestedRef.current = false;
    }
  }, [isLoadingMore, items.length]);

  useEffect(() => {
    const sentinel = loadMoreSentinelRef.current;
    if (!sentinel || !onLoadMore) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (
          entry.isIntersecting &&
          hasNextPage &&
          !isLoadingMore &&
          !isLoading &&
          !loadMoreRequestedRef.current
        ) {
          loadMoreRequestedRef.current = true;
          onLoadMore();
        }
      },
      {
        root: tableContainerRef.current,
        threshold: 0.1,
      }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isLoadingMore, isLoading, onLoadMore]);

  useEffect(() => {
    const updateAvailableHeight = () => {
      if (!paperRef.current) {
        return;
      }

      const tableTop = paperRef.current.getBoundingClientRect().top;
      const availableBottom = getBoundedContainerBottom(paperRef.current);
      const availableHeight = availableBottom - tableTop;
      const nextHeight = fitContentHeight
        ? Math.min(
            availableHeight,
            window.innerHeight * FIT_CONTENT_MAX_VIEWPORT_HEIGHT_RATIO
          )
        : Math.max(MIN_TABLE_HEIGHT, availableHeight);

      setAvailableTableHeight(nextHeight);
    };

    const animationFrame = window.requestAnimationFrame(updateAvailableHeight);
    window.addEventListener("resize", updateAvailableHeight);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.removeEventListener("resize", updateAvailableHeight);
    };
  }, [columns.length, fitContentHeight, items.length]);

  const getTextForSizing = useCallback((column: Column, item: T) => {
    const value = (item as any)[column.field];
    const primitiveValue = getPrimitiveText(value);
    if (primitiveValue !== null) {
      return primitiveValue;
    }

    const tooltipValue = column.tooltipValue?.(value, item);
    return getPrimitiveText(tooltipValue);
  }, []);

  const columnTextMetrics = useMemo(() => {
    return columns.reduce<
      Record<string, { averageLength: number; maxLength: number; sampleCount: number }>
    >((metrics, column) => {
      const lengths = items
        .slice(0, COLUMN_SIZING_SAMPLE_LIMIT)
        .map((item) => getTextForSizing(column, item)?.trim().length ?? 0)
        .filter((length) => length > 0);

      metrics[column.field] = {
        averageLength: lengths.length
          ? lengths.reduce((sum, length) => sum + length, 0) / lengths.length
          : 0,
        maxLength: lengths.length ? Math.max(...lengths) : 0,
        sampleCount: lengths.length,
      };

      return metrics;
    }, {});
  }, [columns, getTextForSizing, items]);

  const hasLongTextValues = (column: Column) => {
    const metrics = columnTextMetrics[column.field];
    return (
      metrics?.maxLength >= LONG_TEXT_COLUMN_LENGTH_THRESHOLD ||
      metrics?.averageLength >= LONG_TEXT_AVERAGE_LENGTH_THRESHOLD
    );
  };

  const hasCompactValues = (column: Column) => {
    const metrics = columnTextMetrics[column.field];
    return (
      metrics?.sampleCount > 0 &&
      metrics.maxLength > 0 &&
      metrics.maxLength <= COMPACT_COLUMN_LENGTH_THRESHOLD
    );
  };

  const getColumnFlex = (column: Column) => {
    if (column.flex !== undefined) {
      return column.flex;
    }
    if (hasLongTextValues(column)) {
      return 1.8;
    }
    if (hasCompactValues(column)) {
      return 0.8;
    }
    return 1;
  };

  const getColumnMinWidth = (column: Column) => {
    if (column.width) {
      return column.width;
    }
    if (column.minWidth) {
      return column.minWidth;
    }
    if (hasLongTextValues(column)) {
      return 220;
    }
    if (hasCompactValues(column)) {
      return 110;
    }
    return 140;
  };

  const tableMinWidth =
    columns.reduce((sum, column) => sum + getColumnMinWidth(column), 0) +
    (hasActions ? ACTIONS_COLUMN_WIDTH : 0) +
    (isRowReorderEnabled ? REORDER_COLUMN_WIDTH : 0);
  const flexibleColumns = columns.filter((column) => !column.width);
  const totalFlex = flexibleColumns.reduce(
    (sum, column) => sum + getColumnFlex(column),
    0
  );
  const getColumnWidth = (column: Column) =>
    column.width
      ? `${column.width}px`
      : `${(getColumnFlex(column) / (totalFlex || columns.length || 1)) * 100}%`;

  const getSkeletonCellWidth = (columnIndex: number) =>
    `${[72, 56, 64, 48][columnIndex % 4]}%`;

  const getTooltipTitle = (column: Column, value: any, row: T) => {
    if (column.disableTooltip) {
      return "";
    }

    const tooltipValue = column.tooltipValue?.(value, row);
    if (isTooltipValuePresent(tooltipValue)) {
      return tooltipValue;
    }

    return "";
  };

  const renderCellContent = (column: Column, item: T) => {
    const value = (item as any)[column.field];
    const content = column.renderCell
      ? column.renderCell(value, item)
      : value;
    const tooltipTitle = getTooltipTitle(column, value, item);
    const fallbackText = getPrimitiveText(value);

    return (
      <TruncatedTooltipCell
        tooltipTitle={tooltipTitle}
        fallbackText={fallbackText}
        maxWidth={column.maxWidth}
      >
        {content}
      </TruncatedTooltipCell>
    );
  };

  const renderColumnGroup = () => (
    <colgroup>
      {isRowReorderEnabled && <col style={{ width: REORDER_COLUMN_WIDTH }} />}
      {displayColumns.map((column) => (
        <col key={column.field} style={{ width: getColumnWidth(column) }} />
      ))}
      {hasActions && <col style={{ width: ACTIONS_COLUMN_WIDTH }} />}
    </colgroup>
  );

  const renderSkeletonRows = () =>
    Array.from({ length: skeletonRowCount }).map((_, rowIndex) => (
      <TableRow key={`skeleton-row-${rowIndex}`}>
        {isRowReorderEnabled && (
          <TableCell
            sx={{
              width: REORDER_COLUMN_WIDTH,
              minWidth: REORDER_COLUMN_WIDTH,
              maxWidth: REORDER_COLUMN_WIDTH,
              px: 1,
            }}
          >
            <Skeleton
              variant="circular"
              width={24}
              height={24}
              sx={{ animationDuration: SKELETON_ANIMATION_DURATION }}
            />
          </TableCell>
        )}
        {displayColumns.map((column, columnIndex) => (
          <TableCell
            key={column.field}
            sx={{
              width: getColumnWidth(column),
              minWidth: getColumnMinWidth(column),
              maxWidth: column.maxWidth,
              px: 1,
            }}
          >
            <Skeleton
              variant="rounded"
              width={getSkeletonCellWidth(columnIndex)}
              height={20}
              sx={{ animationDuration: SKELETON_ANIMATION_DURATION }}
            />
          </TableCell>
        ))}
        {hasActions && (
          <TableCell
            align="center"
            sx={{
              ...stickyActionsCellSx,
              zIndex: 2,
              backgroundColor: "background.paper",
            }}
          >
            <Skeleton
              variant="rounded"
              width={88}
              height={32}
              sx={{ mx: "auto", animationDuration: SKELETON_ANIMATION_DURATION }}
            />
          </TableCell>
        )}
      </TableRow>
    ));

  const renderEmptyState = () => {
    const title = hasActiveSearchFilters ? "No results found" : "No data available";
    const description = hasActiveSearchFilters
      ? "Try adjusting or clearing the filters to see more results."
      : "There are no records to display yet.";

    return (
      <TableRow>
        <TableCell
          colSpan={totalColumns}
          sx={{
            borderBottom: 0,
            height: 220,
            textAlign: "center",
          }}
        >
          <Box
            role="status"
            sx={{
              mx: "auto",
              maxWidth: 360,
              px: 2,
              color: "text.secondary",
            }}
          >
            <Typography variant="subtitle1" color="text.primary" fontWeight={600}>
              {title}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.75 }}>
              {description}
            </Typography>
          </Box>
        </TableCell>
      </TableRow>
    );
  };

  const renderSortIcon = (column: Column) => {
    if (!enableSorting || !column.sortable) return null;
    const isActive = sortModel.field === column.field;
    const iconSx = {
      fontSize: 16,
      ml: 0.5,
      color: isActive ? "primary.main" : "text.disabled",
      transition: "color 0.15s",
    };
    if (isActive && sortModel.direction === "asc")
      return <ArrowUpwardIcon sx={iconSx} />;
    if (isActive && sortModel.direction === "desc")
      return <ArrowDownwardIcon sx={iconSx} />;
    return <SwapVertIcon sx={{ ...iconSx, opacity: 0 }} className="datatable-sort-icon" />;
  };

  const renderHeaderCell = (column: Column) => {
    const searchableColumn = searchableColumns.find(
      (sc) => sc.field === column.field
    );
    const hasActiveFilter = searchFilters[column.field] !== undefined;
    const isSortable = enableSorting && column.sortable;

    return (
      <TableCell
        key={column.field}
        sx={{
          fontWeight: 600,
          py: 1,
          px: 1,
          width: getColumnWidth(column),
          minWidth: getColumnMinWidth(column),
          maxWidth: column.maxWidth,
          backgroundColor: "background.paper",
          verticalAlign: "middle",
          height: 54,
          position: "relative",
          cursor: isSortable ? "pointer" : undefined,
          userSelect: isSortable ? "none" : undefined,
          "&:hover .datatable-sort-icon": { opacity: 0.5 },
          "& .datatable-filter-title": {
            opacity: hasActiveFilter ? 0 : 1,
            transform: hasActiveFilter
              ? "translateY(-4px)"
              : "translateY(0)",
            transition: "opacity 160ms ease, transform 160ms ease",
          },
          "& .datatable-filter-control": {
            opacity: hasActiveFilter ? 1 : 0,
            transform: hasActiveFilter
              ? "translateY(0)"
              : "translateY(4px)",
            pointerEvents: hasActiveFilter ? "auto" : "none",
            transition: "opacity 160ms ease, transform 160ms ease",
          },
          "&:hover .datatable-filter-title, &:focus-within .datatable-filter-title":
            {
              opacity: 0,
              transform: "translateY(-4px)",
            },
          "&:hover .datatable-filter-control, &:focus-within .datatable-filter-control":
            {
              opacity: 1,
              transform: "translateY(0)",
              pointerEvents: "auto",
            },
        }}
        onClick={isSortable ? () => handleSortClick(column.field) : undefined}
      >
        {isSearchEnabled && searchableColumn ? (
          <Box sx={{ position: "relative", minHeight: 40 }}>
            <Box
              className="datatable-filter-title"
              sx={{
                display: "flex",
                alignItems: "center",
                lineHeight: "40px",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                }}
              >
                {column.headerName}
              </Typography>
              {renderSortIcon(column)}
            </Box>
            <Box
              className="datatable-filter-control"
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
              }}
            >
              <SearchInputFactory
                column={{
                  ...searchableColumn,
                  headerName: column.headerName,
                }}
                value={
                  searchFilters[column.field] !== undefined
                    ? searchFilters[column.field]
                    : null
                }
                onChange={(value) =>
                  handleSearchChange(
                    column.field,
                    value,
                    searchableColumn.searchType
                  )
                }
              />
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, lineHeight: 1.2 }}
            >
              {column.headerName}
            </Typography>
            {renderSortIcon(column)}
          </Box>
        )}
      </TableCell>
    );
  };

  const renderHeader = () => {
    const headerCells = (
      <>
        {isRowReorderEnabled && (
          <TableCell
            sx={{
              width: REORDER_COLUMN_WIDTH,
              minWidth: REORDER_COLUMN_WIDTH,
              maxWidth: REORDER_COLUMN_WIDTH,
              py: 1,
              px: 1,
              backgroundColor: "background.paper",
            }}
          />
        )}
        {displayColumns.map((column) => renderHeaderCell(column))}
        {hasActions && (
          <TableCell
            align="center"
            sx={{
              ...stickyActionsCellSx,
              zIndex: 4,
              fontWeight: 600,
              py: 1,
              px: 1,
              backgroundColor: "background.paper",
              verticalAlign: "middle",
            }}
          >
            {isSearchEnabled && searchableColumns.length > 0 ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 700, lineHeight: 1.2 }}
                >
                  Actions
                </Typography>
                {isSearchPending && (
                  <CircularProgress
                    size={16}
                    sx={{ color: "text.secondary" }}
                  />
                )}
              </Box>
            ) : (
              "Actions"
            )}
          </TableCell>
        )}
      </>
    );

    return (
      <TableHead>
        {enableColumnReorder ? (
          <DndContext
            sensors={columnSensors}
            collisionDetection={closestCenter}
            onDragEnd={handleColumnDragEnd}
          >
            <SortableContext
              items={columnOrder}
              strategy={horizontalListSortingStrategy}
            >
              <TableRow>{headerCells}</TableRow>
            </SortableContext>
          </DndContext>
        ) : (
          <TableRow>{headerCells}</TableRow>
        )}
      </TableHead>
    );
  };

  const renderDragHandleCell = (
    dragHandleProps?: {
      attributes: any;
      listeners?: any;
      isDragging: boolean;
    }
  ) => (
    <TableCell
      sx={{
        width: REORDER_COLUMN_WIDTH,
        minWidth: REORDER_COLUMN_WIDTH,
        maxWidth: REORDER_COLUMN_WIDTH,
        px: 1,
      }}
    >
      <IconButton
        size="small"
        aria-label="Drag to reorder row"
        disabled={!dragHandleProps}
        {...dragHandleProps?.attributes}
        {...dragHandleProps?.listeners}
        sx={{
          cursor: dragHandleProps ? "grab" : "default",
          color: "text.secondary",
          "&:active": {
            cursor: dragHandleProps ? "grabbing" : "default",
          },
        }}
      >
        <DragIndicatorIcon fontSize="small" />
      </IconButton>
    </TableCell>
  );

  const renderRowCells = (
    item: T,
    dragHandleProps?: {
      attributes: any;
      listeners?: any;
      isDragging: boolean;
    }
  ) => (
    <>
      {isRowReorderEnabled && renderDragHandleCell(dragHandleProps)}
      {displayColumns.map((column) => (
        <TableCell
          key={column.field}
          sx={{
            width: getColumnWidth(column),
            minWidth: getColumnMinWidth(column),
            maxWidth: column.maxWidth,
            overflow: "hidden",
            px: 1,
          }}
        >
          {renderCellContent(column, item)}
        </TableCell>
      ))}
      {hasActions && (
        <TableCell
          align="center"
          sx={{
            ...stickyActionsCellSx,
            zIndex: 2,
            backgroundColor: "inherit",
            px: 0.5,
            ".MuiTableRow-root:hover &": {
              backgroundColor: "inherit",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 0.5,
            }}
          >
            {(typeof actions === "function" ? actions(item) : actions).map(
              (action, actionIndex) => {
                const isActionDisabled =
                  typeof action.disabled === "function"
                    ? action.disabled(item)
                    : action.disabled;

                return (
                  <Tooltip key={actionIndex} title={action.label}>
                    <IconButton
                      size="small"
                      onClick={() => action.onClick(item)}
                      color={action.color || "primary"}
                      disabled={isActionDisabled}
                      sx={{
                        border: "1px solid",
                        borderColor: `${action.color || "primary"}.main`,
                        height: 28,
                        p: 0,
                        width: 28,
                        "& .MuiSvgIcon-root": {
                          fontSize: "1rem",
                        },
                        "&:hover": {
                          bgcolor: `${action.color || "primary"}.main`,
                          color: "white",
                        },
                      }}
                    >
                      {action.icon}
                    </IconButton>
                  </Tooltip>
                );
              }
            )}
          </Box>
        </TableCell>
      )}
    </>
  );

  const getBaseRowSx = (item: T) => {
    const customRowSx = getRowSx?.(item);
    return [
      {
        backgroundColor: "background.paper",
        "&:last-child td, &:last-child th": { border: 0 },
        "&:hover": { backgroundColor: "action.hover" },
      },
      ...(Array.isArray(customRowSx)
        ? customRowSx
        : customRowSx
          ? [customRowSx]
          : []),
    ] as SxProps<Theme>;
  };

  const renderDataRows = () => {
    if (isGroupingEnabled && grouping) {
      return groupedItems.map((group) => {
        const allowFullCollapse = Boolean(grouping.allowFullCollapse);
        const collapsedItemLimit =
          grouping.collapsedItemLimit ?? (allowFullCollapse ? 0 : group.items.length);
        const isGroupToggled = expandedGroups.has(group.key);
        const isExpanded = grouping.defaultExpanded
          ? !isGroupToggled
          : isGroupToggled;
        const hasMore = group.items.length > collapsedItemLimit;
        const visibleItems =
          hasMore && !isExpanded
            ? group.items.slice(0, collapsedItemLimit)
            : group.items;
        const hiddenCount = group.items.length - collapsedItemLimit;

        return (
          <React.Fragment key={group.key}>
            <TableRow
              hover={allowFullCollapse && hasMore}
              onClick={
                allowFullCollapse && hasMore
                  ? () => toggleGroup(group.key)
                  : undefined
              }
              sx={{
                backgroundColor: "action.selected",
                borderTop: 3,
                borderTopColor: "divider",
                cursor: allowFullCollapse && hasMore ? "pointer" : "default",
              }}
            >
              <TableCell colSpan={totalColumns} sx={{ py: 1, px: 1.5 }}>
                <Box
                  sx={{
                    alignItems: "center",
                    display: "flex",
                    gap: 1,
                    justifyContent: "space-between",
                    minWidth: 0,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    {grouping.renderGroupHeader(group.key, group.items)}
                  </Box>
                  {allowFullCollapse && hasMore && (
                    <IconButton
                      aria-label={isExpanded ? "Collapse group" : "Expand group"}
                      size="small"
                      sx={{ flexShrink: 0 }}
                    >
                      <ExpandMoreIcon
                        sx={{
                          fontSize: 20,
                          transform: isExpanded ? "rotate(180deg)" : "none",
                          transition: "transform 150ms ease",
                        }}
                      />
                    </IconButton>
                  )}
                </Box>
              </TableCell>
            </TableRow>
            {visibleItems.map((item, index) => (
              <TableRow
                key={resolveRowId(item, index)}
                sx={getBaseRowSx(item)}
              >
                {renderRowCells(item)}
              </TableRow>
            ))}
            {hasMore && !allowFullCollapse && (
              <TableRow>
                <TableCell
                  colSpan={totalColumns}
                  sx={{ py: 0.5, px: 1.5, borderBottom: 0 }}
                >
                  <Button
                    size="small"
                    onClick={() => toggleGroup(group.key)}
                    startIcon={
                      <ExpandMoreIcon
                        sx={{
                          fontSize: 18,
                          transform: isExpanded ? "rotate(180deg)" : "none",
                        }}
                      />
                    }
                    sx={{
                      fontSize: "0.75rem",
                      textTransform: "none",
                      color: "text.secondary",
                      "&:hover": { color: "primary.main" },
                    }}
                  >
                    {isExpanded
                      ? "Show less"
                      : `Show ${hiddenCount} more ${groupedItemLabel}${
                          hiddenCount !== 1 ? "s" : ""
                        }`}
                  </Button>
                </TableCell>
              </TableRow>
            )}
          </React.Fragment>
        );
      });
    }

    if (!isRowReorderEnabled) {
      return sortedItems.map((item, index) => (
        <TableRow key={resolveRowId(item, index)} sx={getBaseRowSx(item)}>
          {renderRowCells(item)}
        </TableRow>
      ));
    }

    return (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={sortableIds}
          strategy={verticalListSortingStrategy}
        >
          {sortedItems.map((item, index) => {
            const rowId = resolveRowId(item, index);
            return (
              <SortableTableRow
                key={rowId}
                id={rowId}
                sx={getBaseRowSx(item)}
              >
                {(dragHandleProps) => renderRowCells(item, dragHandleProps)}
              </SortableTableRow>
            );
          })}
        </SortableContext>
      </DndContext>
    );
  };

  return (
    <Paper
      ref={paperRef}
      sx={{
        position: "relative",
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        height: fitContentHeight
          ? "auto"
          : availableTableHeight
            ? `${availableTableHeight}px`
            : "calc(100dvh - 140px)",
        maxHeight: fitContentHeight
          ? availableTableHeight
            ? `${availableTableHeight}px`
            : "calc(100dvh - 140px)"
          : undefined,
        minHeight: fitContentHeight ? undefined : MIN_TABLE_HEIGHT,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        borderRadius: 1,
        boxShadow: "0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)",
      }}
    >
      {toolbar && (
        <Box sx={{ flexShrink: 0, borderBottom: 1, borderColor: "divider" }}>
          {toolbar}
        </Box>
      )}
      <Box
        ref={headerContainerRef}
        sx={{
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          flexShrink: 0,
          overflowX: "hidden",
          overflowY: "hidden",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Table sx={{ tableLayout: "fixed", minWidth: tableMinWidth }}>
          {renderColumnGroup()}
          {renderHeader()}
        </Table>
      </Box>
      <TableContainer
        ref={tableContainerRef}
        onScroll={handleBodyScroll}
        sx={{
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          flex: fitContentHeight ? "0 1 auto" : 1,
          minHeight: 0,
          overflow: "auto",
          overscrollBehaviorX: "contain",
        }}
      >
        <Table sx={{ tableLayout: "fixed", minWidth: tableMinWidth }}>
          {renderColumnGroup()}
          <TableBody aria-busy={showRowSkeletons}>
            {showRowSkeletons
              ? renderSkeletonRows()
              : items.length === 0
                ? renderEmptyState()
                : renderDataRows()}
            {!showRowSkeletons && onLoadMore && !isRowReorderEnabled && (
              <TableRow ref={loadMoreSentinelRef}>
                <TableCell
                  colSpan={totalColumns}
                  sx={{
                    textAlign: isLoadingMore ? "center" : "left",
                    py: 1.5,
                    border: 0,
                  }}
                >
                  {isLoadingMore ? (
                    <CircularProgress size={24} />
                  ) : !hasNextPage && items.length > 0 ? (
                    <Typography variant="caption" color="text.secondary">
                      All results loaded
                    </Typography>
                  ) : null}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box
        sx={{
          borderTop: 1,
          borderColor: "divider",
          px: 2,
          py: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
          minWidth: 0,
          maxWidth: "100%",
          overflow: "hidden",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          {isSearchEnabled && hasActiveSearchFilters && (
            <Fade in>
              <Chip
                size="small"
                color="error"
                icon={<FilterAltOffIcon />}
                label="Clear filters"
                onClick={clearAllFilters}
                sx={{
                  bgcolor: "error.main",
                  border: 1,
                  borderColor: "error.main",
                  color: "error.contrastText",
                  fontWeight: 600,
                  "& .MuiChip-icon": {
                    color: "error.contrastText",
                  },
                  "&:hover": {
                    bgcolor: "error.dark",
                    color: "error.contrastText",
                    "& .MuiChip-icon": {
                      color: "error.contrastText",
                    },
                  },
                }}
              />
            </Fade>
          )}
        </Box>
        <Box
          sx={{
            ml: "auto",
            display: "flex",
            alignItems: "center",
            gap: 1,
            minWidth: 0,
          }}
        >
          {isLoadingMore && <CircularProgress size={18} />}
          <Typography variant="body2" color="text.secondary" textAlign="right">
            {isGroupingEnabled && groupingCountMode === "groups"
              ? `Showing ${groupedItems.length} of ${displayedGroupTotalCount} ${groupLabel}`
              : `Showing ${items.length} of ${displayedTotalCount} results`}
          </Typography>
        </Box>
      </Box>
      {onPageChange && !onLoadMore && !isRowReorderEnabled && (
        <TablePagination
          sx={{ flexShrink: 0, maxWidth: "100%", overflow: "hidden" }}
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={pageSize}
          onRowsPerPageChange={handlePageSizeChange}
          rowsPerPageOptions={rowsPerPageOptions}
        />
      )}
    </Paper>
  );
};

// Helper functions for easier DataTable usage
export const createColumn = (
  field: string,
  headerName: string,
  options?: Partial<Column>
): Column => ({
  field,
  headerName,
  ...options,
});

export const createSearchableColumn = (
  field: string,
  headerName: string,
  searchType?: "string" | "number" | "date",
  options?: Partial<Column>
): SearchableColumn => ({
  field,
  headerName,
  searchable: true,
  searchType: searchType || "string",
  ...options,
});

// Helper to create searchable columns from regular columns
export const makeColumnsSearchable = (
  columns: Column[],
  searchableFields: string[],
  searchTypes?: Record<string, "string" | "number" | "date">
): SearchableColumn[] => {
  return columns
    .filter((col) => searchableFields.includes(col.field))
    .map((col) =>
      createSearchableColumn(
        col.field,
        col.headerName,
        searchTypes?.[col.field],
        col
      )
    );
};
